import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface BadgeProps {
  children: ReactNode;
  variant?: 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'slate' | 'neutral';
  size?: 'sm' | 'md';
  dot?: boolean;
  className?: string;
}

const VARIANTS = {
  brand: 'bg-brand-50 text-brand dark:bg-brand-50/10 dark:text-brand-tint border-brand/20',
  success: 'bg-success-50 text-success dark:bg-emerald-950/40 dark:text-emerald-400 border-success/20',
  warning: 'bg-warning-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-warning/20',
  danger: 'bg-danger-50 text-danger dark:bg-rose-950/40 dark:text-rose-300 border-danger/20',
  info: 'bg-info-50 text-info dark:bg-blue-950/40 dark:text-blue-400 border-info/20',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  neutral: 'bg-slate-50 text-ink-3 dark:bg-slate-800/60 dark:text-slate-400 border-slate-200/60 dark:border-slate-700',
};

const DOT_COLORS = {
  brand: 'bg-brand',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  slate: 'bg-slate-500',
  neutral: 'bg-slate-400',
};

export function Badge({
  children,
  variant = 'slate',
  size = 'md',
  dot = false,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-semibold',
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs',
        VARIANTS[variant],
        className,
      )}
    >
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_COLORS[variant])}
          aria-hidden
        />
      )}
      <span>{children}</span>
    </span>
  );
}

export default Badge;
